import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { MUNICIPIOS_IDS } from '../../constants/municipios'
import { ComponentRenderer } from '../ComponentRenderer'
import Select from 'react-select'

interface Props {
  activeCategory: any | null
  onSelectCategory: (cat: any) => void
}

interface Municipio {
  id_municipio: string
  nombre: string
}

// Interface para los componentes del dashboard
interface DashboardComponent {
  id: number
  dashboard_id: number
  component_id: number
  grid_x: number
  grid_y: number
  grid_column: number
  grid_row: number
  components: {
    id: number
    name: string
    type: string
    configuration: any
  }
}

interface Dashboard {
  id: number;
  name: string;
  slug: string | null;
  description: string | null;
}

export default function DatosTematicos({ activeCategory, onSelectCategory }: Props) {
  const [categories, setCategories] = useState<any[]>([])
  const [municipios, setMunicipios] = useState<Municipio[]>([])
  const [dashboard, setDashboard] = useState<any>(null)
  const [dashboardComponents, setDashboardComponents] = useState<DashboardComponent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMunicipios, setLoadingMunicipios] = useState(false)
  const [loadingDashboard, setLoadingDashboard] = useState(false)
  const [selectedMunicipio, setSelectedMunicipio] = useState<string | null>(null)

  useEffect(() => {
    loadCategories()
    loadMunicipios()
  }, [])

  useEffect(() => {
    if (activeCategory) {
      loadDashboardForCategory(activeCategory.id)
    } else {
      setDashboard(null)
      setDashboardComponents([])
    }
  }, [activeCategory])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('show_tematic_data', true)
        .is('parent_id', null) // Solo categorías padre
        .order('orderlevel', { ascending: true })

      if (error) throw error
      setCategories(data || [])
    } catch (err) {
      console.error('Error cargando categorias tematicas', err)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const loadMunicipios = async () => {
    try {
      setLoadingMunicipios(true)
      const { data, error } = await supabase
        .from('municipios')
        .select('id_municipio, nombre')
        .in('id_municipio', MUNICIPIOS_IDS)
        .order('nombre', { ascending: true })

      if (error) throw error
      setMunicipios(data || [])
    } catch (err) {
      console.error('Error cargando municipios', err)
      setMunicipios([])
    } finally {
      setLoadingMunicipios(false)
    }
  }

  const loadDashboardForCategory = async (categoryId: number) => {
    try {
      setLoadingDashboard(true)
      
      // 1. Cargar el dashboard (solo uno por categoría)
      const { data: dashboardData, error: dashboardError } = await supabase
        .from('dashboards')
        .select('id, name, slug, description')
        .eq('category_id', categoryId)
        .is('subcategory_id', null) // Solo categorías padre
        .limit(1)
        .single<Dashboard>()

      if (dashboardError) {
        if (dashboardError.code === 'PGRST116') { // No encontrado
          setDashboard(null)
          setDashboardComponents([])
          return
        }
        throw dashboardError
      }

      setDashboard(dashboardData)

      // 2. Cargar los componentes del dashboard
      const { data: componentsData, error: componentsError } = await supabase
        .from('dashboard_components')
        .select(`
          id,
          dashboard_id,
          component_id,
          grid_x,
          grid_y,
          grid_column,
          grid_row,
          components (
            id,
            name,
            type,
            configuration
          )
        `)
        .eq('dashboard_id', dashboardData.id)
        .order('grid_y')
        .order('grid_x')

      if (componentsError) throw componentsError
      setDashboardComponents(componentsData || [])

    } catch (err) {
      console.error('Error cargando dashboard por categoria', err)
      setDashboard(null)
      setDashboardComponents([])
    } finally {
      setLoadingDashboard(false)
    }
  }

  const municipiosOptions = municipios.map(m => ({
    value: m.id_municipio,
    label: m.nombre,
  }));

  const selectedOption = selectedMunicipio 
    ? municipiosOptions.find(opt => opt.value === selectedMunicipio)
    : null

  // Ordenar componentes por grid_y y grid_x
  const sortedComponents = [...dashboardComponents].sort((a, b) => {
    if (a.grid_y === b.grid_y) {
      return a.grid_x - b.grid_x
    }
    return a.grid_y - b.grid_y
  })

  if (loading) {
    return (
      <div className="min-h-40 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bmp-purple mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando categorías...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card border rounded-lg p-6">
      <div className="border rounded-lg p-4 transition-all">
        <div className="flex gap-2 overflow-x-auto pb-3">
          {categories.map(cat => {
            const active = activeCategory?.id === cat.id
            return (
              <button
                key={cat.id}
                onClick={() => onSelectCategory(cat)}
                className={`px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all border ${active ? 'bg-green-600 text-white border-green-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
              >
                {cat.name}
              </button>
            )
          })}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
          {activeCategory ? (
            <>
              <div className="mb-6">
                <h3 className="font-semibold text-xl">{activeCategory.name}</h3>
                {activeCategory.description && (
                  <p className="text-gray-600 mt-2">{activeCategory.description}</p>
                )}
              </div>

              {loadingDashboard ? (
                <div className="min-h-40 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Cargando dashboard...</p>
                  </div>
                </div>
              ) : !dashboard ? (
                <div className="text-center py-10">
                  <div className="text-5xl mb-4">📊</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay dashboard disponible</h3>
                  <p className="text-gray-500">Esta categoría no tiene un dashboard asociado.</p>
                </div>
              ) : (
                <div>
                  {dashboard.description && (
                    <p className="text-gray-700 mb-6">{dashboard.description}</p>
                  )}

                  {/* Selector de municipio */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filtrar por municipio
                    </label>
                    <div className="relative"> 
                      <Select
                          className="basic-single"
                          classNamePrefix="select"
                          value={selectedOption}
                          onChange={(option: any) => setSelectedMunicipio(option ? option.value : null)}
                          options={municipiosOptions}
                          isClearable={true}
                          placeholder="Todos los municipios"
                          isDisabled={loadingMunicipios}
                          styles={{
                            control: (base) => ({
                              ...base,
                              maxWidth: '20rem',
                            }),
                          }}
                        />
                    </div>
                    {loadingMunicipios && (
                      <p className="text-sm text-gray-500 mt-1">Cargando municipios...</p>
                    )}
                    {selectedMunicipio && (
                      <p className="text-sm text-gray-600 mt-2">
                        Mostrando datos para: {municipios.find(m => m.id_municipio === selectedMunicipio)?.nombre}
                      </p>
                    )}
                  </div>

                  {/* Grid del dashboard */}
                  <div className="mx-auto" style={{ maxWidth: '1400px' }}>
                    {sortedComponents.length === 0 ? (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                        <div className="text-6xl mb-4">📦</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Dashboard vacío</h3>
                        <p className="text-gray-500">Este dashboard no tiene componentes aún.</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop: grid 12 cols */}
                        <div className="hidden lg:grid gap-4" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gridAutoRows: '120px' }}>
                          {sortedComponents.map((dashComp) => {
                            const c = dashComp.components
                            return (
                              <div 
                                key={dashComp.id} 
                                style={{ 
                                  gridColumn: `${dashComp.grid_x + 1} / span ${dashComp.grid_column}`, 
                                  gridRow: `${dashComp.grid_y + 1} / span ${dashComp.grid_row}` 
                                }}
                              >
                                <ComponentRenderer 
                                  component={{ 
                                    id: c.id, 
                                    name: c.name, 
                                    type: c.type as any, 
                                    configuration: {
                                      ...c.configuration, // Mantener la configuración original
                                      filterMunicipio: selectedMunicipio || null,
                                    },
                                  }} 
                                  filterMunicipio={selectedMunicipio}
                                  filterEspecie={0}
                                  filterMunicipioNombre={selectedMunicipio ? 
                                    municipios.find(m => m.id_municipio == selectedMunicipio)?.nombre || null 
                                    : null}
                                />
                              </div>
                            )
                          })}
                        </div>

                        {/* Mobile: vertical */}
                        <div className="flex flex-col gap-6 lg:hidden">
                          {sortedComponents.map((dashComp) => {
                            const c = dashComp.components
                            return (
                              <div key={dashComp.id} className="w-full">
                                <ComponentRenderer 
                                  component={{ 
                                    id: c.id, 
                                    name: c.name, 
                                    type: c.type as any, 
                                    configuration: {
                                      ...c.configuration, // Mantener la configuración original
                                      filterMunicipio: selectedMunicipio || null,
                                    },
                                    
                                  }} 
                                  filterMunicipio={selectedMunicipio}
                                  filterEspecie={0}
                                  filterMunicipioNombre={selectedMunicipio ? 
                                    municipios.find(m => m.id_municipio == selectedMunicipio)?.nombre || null 
                                    : null}
                                />
                              </div>
                            )
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-10">
              <div className="text-5xl mb-4">📈</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Selecciona una categoría</h3>
              <p className="text-gray-500">Elige una categoría del listado para ver su dashboard temático.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}